import { useEffect, useState } from 'react';
import type { CargoRes } from 'api/swagger';
import type { CargoAddressRes } from 'api/swagger';
import { addressStatus } from 'shared/utils/address-status';
import { calculateCostPerItem } from 'shared/utils/cargos/calculate-cost-per-item';
import { EMapMarkerType } from 'types/map';
import type { IMapMarkersPayload } from 'components/map/map-container/hooks/types';
import { ESetCargosMarkersMode } from 'components/map/map-container/hooks/types';

const createLatLngString = (lat: number, lng: number): string => `${lat}/${lng}`;

export const useCreateMarkerArray = (cargos: CargoRes[], setMode: ESetCargosMarkersMode): IMapMarkersPayload[] => {
  const [result, setResult] = useState<Set<IMapMarkersPayload>>(new Set());

  useEffect(() => {
    const tmpCoordinates = new Set<string>();
    const newSet = new Set<IMapMarkersPayload>();

    cargos.forEach((cargo) => {
      const addresses: { data: CargoAddressRes; type: EMapMarkerType }[] = [];

      if (setMode === ESetCargosMarkersMode.LOADING || setMode === ESetCargosMarkersMode.FULL) {
        addresses.push({ data: cargo.loadingAddress, type: EMapMarkerType.LOADING });
        if (cargo.loadingCustomAddress) {
          addresses.push({ data: cargo.loadingCustomAddress, type: EMapMarkerType.CUSTOM });
        }
      }

      if (setMode === ESetCargosMarkersMode.UNLOADING || setMode === ESetCargosMarkersMode.FULL) {
        addresses.push({ data: cargo.unloadingAddress, type: EMapMarkerType.UNLOADING });
        if (cargo.unloadingCustomAddress) {
          addresses.push({ data: cargo.unloadingCustomAddress, type: EMapMarkerType.CUSTOM });
        }
      }

      addresses.forEach((address) => {
        if (address.data.lat && address.data.lng) {
          const latLngString = createLatLngString(address.data.lat, address.data.lng);

          const addressEmoji = addressStatus(address.data.googleLocationType, {
            withoutLike: true,
            detectionType: address.data.detectionType,
          });

          const title = `${cargo.compositeId} ${addressEmoji} `;

          const costPrice = calculateCostPerItem(cargo);

          const costValue = address.type !== EMapMarkerType.CUSTOM && costPrice ? costPrice : undefined;

          if (tmpCoordinates.has(latLngString)) {
            const fakeLat = address.data.lat + (Math.random() - 0.5) / 1500;
            const fakeLng = address.data.lng + (Math.random() - 0.5) / 1500;

            newSet.add({
              cargoId: cargo.id,
              type: address.type,
              costValue,
              position: {
                lat: fakeLat,
                lng: fakeLng,
              },
              realPosition: {
                lat: address.data.lat,
                lng: address.data.lng,
              },
              title,
              isChangedPosition: true,
            });

            tmpCoordinates.add(createLatLngString(fakeLat, fakeLng));
          } else {
            newSet.add({
              cargoId: cargo.id,
              type: address.type,
              costValue,
              position: {
                lat: address.data.lat,
                lng: address.data.lng,
              },
              realPosition: {
                lat: address.data.lat,
                lng: address.data.lng,
              },
              title,
              isChangedPosition: false,
            });

            tmpCoordinates.add(createLatLngString(address.data.lat, address.data.lng));
          }
        }
      });
    });

    setResult(newSet);
  }, [cargos, setMode]);

  return Array.from(result.values());
};
